import { useCallback, useReducer, useEffect, useMemo } from 'react';
import { Link, useLocation } from "react-router-dom";
import { notification, Button, Space, Tag, Modal, Form, Select, Alert, DatePicker, Switch, Spin } from 'antd';
import { MobileOutlined, ExclamationCircleOutlined, CloseOutlined, CheckOutlined, FilterFilled, AlertOutlined, WarningOutlined } from '@ant-design/icons';
import { getTokenInfo, hasPrivilege } from "../../api/token.js";
import { KICONEX_UUID } from "../../utils/constants.js";
import service from '../../services/Service.js';
import { withLocale } from '../../l10n/index.jsx';
import DataTable from '../DataTable/index.js';
import BarChart from '../BarChart/BarChart.jsx';
import dayjs from 'dayjs';
import withNavigation from '../hooks/withNavigation.jsx';
import Excelexport from '../../utils/excelexport.jsx';
import { GetColumnLiteralSearchProps } from '../../utils/functions.jsx';
import { formatDate as formatDay, formatDateTime } from '../../utils/dates';
import { useGetTenantsLookupQuery, useGetPoolsLookupQuery } from '../../services/lookupApi.js';

const { RangePicker } = DatePicker;

// ===== CONSTANTES GLOBALES =====
const getAlarmsState = (lang) => [
  { text: lang.handle("Sin alertas"), value: 0 },
  { text: lang.handle("Advertencia"), value: 1 },
  { text: lang.handle("Critica"), value: 2 }
];

const getAlarmsTags = (lang) => [
  {
    label: <div key="warning"><Tag color='yellow' className='m-2' icon={<WarningOutlined />}>{lang.handle('Advertencia')}</Tag></div>,
    value: 1
  },
  {
    label: <div key="critical"><Tag color='red' className='m-2' icon={<AlertOutlined />}>{lang.handle('Critica')}</Tag></div>,
    value: 2
  },
];

function getCurrentDate() {
  return dayjs().format('YYYY-MM-DD');
}

function getDateThreeMonthsAgo() {
  return dayjs().subtract(3, 'month').format('YYYY-MM-DD');
}

const getInitialState = () => ({
  // UI
  loading: true,
  showModal: false,
  showModalState: false,
  showModalConsume: false,
  showModalPool: false,
  featuresGSM: { background: 'red', text: 'Error', icon: '' },
  featuresGPRS: { background: 'red', text: 'Error', icon: '' },
  
  // Data
  datos: [],
  iccAlerts: [],
  alertDescription: "",
  stateData: { responseGSM: '', responseGPRS: '' },
  tenants: [],
  
  // Filters
  activeFilters: {},
  filteredRows: [],
  poolNamesFilter: null,
  
  // Modal Data
  selectedIds: [],
  lifeCycle: '',
  dates: null,
  consumes: null,
  selectedPool: null,
  
  // Params
  params: {
    start_date: getDateThreeMonthsAgo(),
    end_date: getCurrentDate(),
    monthly: true,
  }
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_DATOS':
      return { ...state, datos: action.payload };
    
    case 'SET_SELECTED_IDS':
      return { ...state, selectedIds: action.payload };
    
    case 'SET_LIFE_CYCLE':
      return { ...state, lifeCycle: action.payload };
    
    case 'SHOW_MODAL':
      return { ...state, showModal: action.payload };
    
    case 'SHOW_MODAL_STATE':
      return { 
        ...state, 
        showModalState: action.payload.show,
        stateData: action.payload.stateData || state.stateData,
        featuresGSM: action.payload.gsm || state.featuresGSM,
        featuresGPRS: action.payload.gprs || state.featuresGPRS
      };
    
    case 'SHOW_MODAL_CONSUME':
      return { ...state, showModalConsume: action.payload };
    
    case 'SET_CONSUMES':
      return { 
        ...state, 
        consumes: action.payload.consumes,
        dates: action.payload.dates
      };
    
    case 'SET_PARAMS':
      return { ...state, params: action.payload };
    
    case 'SET_TENANTS':
      return { ...state, tenants: action.payload };
    
    case 'SET_ICC_ALERTS':
      return { 
        ...state, 
        iccAlerts: action.payload.alerts,
        alertDescription: action.payload.description
      };
    
    case 'SET_TABLE_STATE':
      return {
        ...state,
        activeFilters: action.payload.filters,
        filteredRows: action.payload.filteredRows
      };
    
    case 'SET_POOL_NAMES_FILTER':
      return { ...state, poolNamesFilter: action.payload };
    
    case 'RESET_MODAL':
      return {
        ...state,
        showModal: false,
        showModalState: false,
        showModalConsume: false,
        showModalPool: false,
        selectedPool: null,
        lifeCycle: ''
      };
    
    case 'SHOW_MODAL_POOL':
      return { ...state, showModalPool: action.payload };

    case 'SET_SELECTED_POOL':
      return { ...state, selectedPool: action.payload };

    default:
      return state;
  }
};

const EMPTY_ARRAY = [];

function M2MList ({ lang }) {
  // ===== 1. ESTADOS =====
  const [state, dispatch] = useReducer(reducer, getInitialState());

  // ===== 2. SELECTORES =====
  const location = useLocation();
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();
  const { data: pools = EMPTY_ARRAY } = useGetPoolsLookupQuery();

  // ===== 3. DERIVADOS =====
  const M2M_ALARMS_STATE = useMemo(() => getAlarmsState(lang), [lang]);
  const M2M_ALARMS_TAG = useMemo(() => getAlarmsTags(lang), [lang]);
  const singleSelectDisabled = state.selectedIds.length !== 1;
  const poolButtonDisabled = state.selectedIds.length === 0;
  const poolOptions = useMemo(() =>
    pools.map(pool => ({
      label: pool.commercialGroup,
      value: pool.pool_id
    })), [pools]);

  // ===== 4. HELPERS =====
  const formatAlarms = useCallback((alarmArray) => {
    if (!alarmArray || alarmArray.length === 0) return '';
    const alarmLabels = {
      1: lang.handle('Advertencia'),
      2: lang.handle('Critica')
    };
    return alarmArray.map(value => alarmLabels[value] || '').filter(Boolean).join(', ');
  }, [lang]);

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${((bytes / 1024) / 1024).toFixed(2)} MB`;
    return `${(((bytes / 1024) / 1024) / 1024).toFixed(2)} GB`;
  }

  const formatDate = (dateString, withTime = false) => {
    const text = withTime ? formatDateTime(dateString) : formatDay(dateString);
    return text || lang.handle('Sin información');
  }

  const formatRatType = (ratType) => {
    switch (ratType) {
      case 1: return '3G';
      case 2: return '2G';
      case 5: return '3.5G';
      case 6: return '4G';
      case 8: return 'NB-IoT';
      default: return lang.handle('Sin información');
    }
  }

  const hasMoreThanOneMonthDifference = (date1, date2) => {
    return Math.abs(dayjs(date2).diff(dayjs(date1), 'month', true)) > 1;
  }

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    const tenant_uuid = getTokenInfo().tenant_uuid;
    const fetchParams = { tenant_uuid };

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const [m2mResult, alertsResult] = await Promise.allSettled([
        service.get('m2m', fetchParams),
        service.get('m2m/deleted-risk', fetchParams)
      ]);

      if (m2mResult.status === 'fulfilled') {
        const m2mRes = m2mResult.value;
        if (m2mRes.data.ok === false) {
          notification["error"]({ message: lang.handle(m2mRes.data.message) });
          dispatch({ type: 'SET_DATOS', payload: [] });
        } else {
          const datos = m2mRes.data.map(element => {
            const customField1 = element.customField1 
              ? element.customField1.toUpperCase() 
              : element.customField1;
            
            return {
              ...element,
              device_name: element.device_name || lang.handle("Sin asignar"),
              customField1
            };
          });
          dispatch({ type: 'SET_DATOS', payload: datos });
        }
      } else {
        console.error('Fallo carga de M2M:', m2mResult.reason);
        notification["error"]({ message: lang.handle('Error al obtener datos') });
        dispatch({ type: 'SET_DATOS', payload: [] });
      }

      if (alertsResult.status === 'fulfilled') {
        const alertsRes = alertsResult.value;
        if (alertsRes.data.ok === false) {
          notification["error"]({ message: lang.handle(alertsRes.data.message) });
          dispatch({ type: 'SET_ICC_ALERTS', payload: { alerts: [], description: "" } });
        } else {
          dispatch({ type: 'SET_ICC_ALERTS', payload: { alerts: alertsRes.data, description: "" } });
        }
      } else {
        console.warn('Falló carga de alertas');
        dispatch({ type: 'SET_ICC_ALERTS', payload: { alerts: [], description: "" } });
      }
    } catch (err) {
      console.error('Error fetching M2M data:', err);
      notification["error"]({ message: lang.handle('Error general al obtener datos') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [lang]);

  const showGrafic = useCallback(async (currentParams) => {
    const fetchParams = currentParams || state.params;

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const res = await service.post(`m2m/${state.selectedIds[0]}/consumes`, fetchParams);

      if (res && res.data && res.data.ok !== false) {
        const dates = res.data.map(data => data.date);
        const consumes = res.data.map(data => data.data);
        dispatch({ type: 'SET_CONSUMES', payload: { dates, consumes } });
      }
    } catch (error) {
      console.error('Error fetching consumes data:', error);
      notification["error"]({ message: lang.handle('Error al obtener datos') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }

  }, [lang, state.selectedIds, state.params]);

  const initCatalogDerivedState = useCallback(() => {
    const poolNamesFilter = pools.map(pool => ({
      text: pool.commercialGroup,
      value: pool.commercialGroup
    }));

    dispatch({ type: 'SET_POOL_NAMES_FILTER', payload: poolNamesFilter });

    const tokenInfo = getTokenInfo();
    const tenant_uuid = tokenInfo.tenant_uuid;
    const is_admin = tokenInfo.is_admin;

    const filteredTenants = is_admin || tenant_uuid === KICONEX_UUID
      ? tenants
      : tenants.filter(t => t.uuid === tenant_uuid);

    dispatch({ type: 'SET_TENANTS', payload: filteredTenants });
  }, [pools, tenants]);

  // SIM seleccionada en el modal de cambio de estado
  const selectedSim = useMemo(
    () => state.datos.find(sim => sim.icc === state.selectedIds[0]) || null,
    [state.datos, state.selectedIds]
  );

  // Estados a los que se puede pasar segun el estado actual de la SIM.
  const lifeCycleOptions = useMemo(() => {
    const LABELS = {
      ACTIVE: lang.handle('Activar'),
      DEACTIVATED: lang.handle('Desactivar'),
      TEST: lang.handle('Test'),
      ACTIVATION_READY: lang.handle('Lista para activación')
    };

    const TRANSITIONS = {
      INACTIVE_NEW: ['ACTIVE', 'DEACTIVATED', 'TEST', 'ACTIVATION_READY'],
      TEST: ['ACTIVE', 'DEACTIVATED', 'ACTIVATION_READY'],
      ACTIVATION_READY: ['ACTIVE', 'DEACTIVATED'],
      ACTIVE: ['ACTIVE', 'DEACTIVATED'],
      DEACTIVATED: ['ACTIVE', 'DEACTIVATED']
    };

    // Estado desconocido
    const allowed = TRANSITIONS[selectedSim?.lifeCycleStatus] ?? Object.keys(LABELS);

    return allowed.map(value => ({ value, label: LABELS[value] }));
  }, [selectedSim, lang]);

  // ===== 6. HANDLERS =====
  const showModal = async (e) => {
    const id = e.currentTarget.id;
    const selectedId = state.selectedIds[0];

    if (id !== "consume" && state.selectedIds.length !== 1) {
      notification["error"]({ message: lang.handle('Selecciona una SIM primero') });
      return;
    }

    if (id === "lifeCycle") {
      dispatch({ type: 'SHOW_MODAL', payload: true });
    } else if (id === "state") {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        const res = await service.get('m2m/status/' + selectedId);
        
        if (res.data.ok === false) {
          notification["error"]({ message: lang.handle(res.data.message) });
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        const featuresGSM = res.data.responseGSM.result === 'GSM_UP'
          ? { background: "green", text: "OK", icon: <CheckOutlined /> }
          : { background: "red", text: lang.handle('Error'), icon: <CloseOutlined /> };
        
        const featuresGPRS = res.data.responseGPRS.result === 'GPRS_UP'
          ? { background: "green", text: "OK", icon: <CheckOutlined /> }
          : { background: "red", text: lang.handle('Error'), icon: <CloseOutlined /> };

        dispatch({ 
          type: 'SHOW_MODAL_STATE', 
          payload: {
            show: true,
            stateData: res.data,
            gsm: featuresGSM,
            gprs: featuresGPRS
          }
        });
        dispatch({ type: 'SET_LOADING', payload: false });
      } catch (error) {
        console.error('Error fetching state data:', error);
        notification["error"]({ message: lang.handle('Error al obtener datos') });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } else if (id === "consume") {
      const initialParams = {
        start_date: getDateThreeMonthsAgo(),
        end_date: getCurrentDate(),
        monthly: true,
      };
      dispatch({ type: 'SET_PARAMS', payload: initialParams });
      dispatch({ type: 'SHOW_MODAL_CONSUME', payload: true });
      await showGrafic(initialParams);
    }
  };

  const handleCancel = () => {
    dispatch({ type: 'RESET_MODAL' });
  };

  const handleChange = (value) => {
    dispatch({ type: 'SET_LIFE_CYCLE', payload: value });
  }

  const onSubmit = async (e) => {
    if (e) e.preventDefault();

    const selectedId = state.selectedIds[0];
    
    if (!selectedId || !state.lifeCycle) {
      notification["error"]({ message: lang.handle('Por favor completa todos los campos') });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const simData = { 
        lifeCycleStatus: state.lifeCycle, 
        icc: selectedId 
      };
      await service.put('m2m/' + simData.icc, simData);
      notification["success"]({ message: lang.handle('SIM actualizada correctamente') });
      handleCancel();
      await Pfetch();
    } catch (error) {
      console.error('Error updating SIM:', error);
      notification["error"]({ message: lang.handle('Error en la operación') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }

  const onSubmitPool = async () => {
    if (state.selectedIds.length === 0 || !state.selectedPool) {
      notification["error"]({ message: lang.handle('Selecciona tarjetas y una tarifa') });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const payload = { iccs: state.selectedIds, poolId: state.selectedPool };
      const res = await service.put('m2m/change-pool', payload);

      if (res?.data?.ok === true) {
        // Éxito total
        notification["success"]({ message: lang.handle('Pools actualizados correctamente') });
      } else if (res?.data?.ok === false && res?.data?.updated?.length > 0) {
        // Resultado parcial: algunos se actualizaron, otros fallaron
        notification["warning"]({
          message: lang.handle('Algunas tarjetas no se pudieron actualizar'),
          description: `${lang.handle('Actualizadas')}: ${res.data.updated.length} · ${lang.handle('Fallidas')}: ${res.data.failed.length}`
        });
      } else {
        // Error total o respuesta inesperada
        notification["error"]({ message: lang.handle('No se pudieron actualizar los datos') });
      }

      dispatch({ type: 'SET_SELECTED_IDS', payload: [] });
      handleCancel();
      await Pfetch();
    } catch (error) {
      console.error('Error updating pool:', error);
      notification["error"]({ message: lang.handle('Error en la operación') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleTableChange = useCallback((pagination, filters, sorter, extra) => {
    dispatch({
      type: 'SET_TABLE_STATE',
      payload: {
        filters,
        filteredRows: extra.currentDataSource
      }
    });
  }, []);

  const openInfo = useCallback(() => {
    const infoLinks = state.iccAlerts.map(e => (
      <Link className='iccLabel' key={e.icc} to={"/redirect/m2m/" + e.icc} style={{ textDecoration: 'none' }}>
        <span className="nav-text"> {e.icc} </span>
      </Link>
    ));
    dispatch({
      type: 'SET_ICC_ALERTS',
      payload: {
        alerts: state.iccAlerts,
        description: infoLinks
      }
    });
  }, [state.iccAlerts]);

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const start_date = dates[0].format('YYYY-MM-DD');
      const end_date = dates[1].format('YYYY-MM-DD');
      const monthly = hasMoreThanOneMonthDifference(start_date, end_date);
      
      const newParams = { ...state.params, start_date, end_date, monthly };
      dispatch({ type: 'SET_PARAMS', payload: newParams });
      showGrafic(newParams);
    }
  };

  const getExportData = () => {
    const sourceData = state.filteredRows.length > 0 ? state.filteredRows : state.datos;
    
    return sourceData.map(row => ({
      ALIAS: row.alias || '',
      ORGANIZACIÓN: row.customField1 || '',
      ICC: row.icc || '',
      'INSTALACIÓN ORIGINAL': row.device_name || '',
      MSISDN: row.msisdn || '',
      ESTADO: row.lifeCycleStatus || '',
      ALARMAS: formatAlarms(row.alarms),
      'CONSUMO DIARIO': formatBytes(row.consumptionDaily?.data?.value || 0),
      'CONSUMO MENSUAL': formatBytes(row.consumptionMonthly?.data?.value || 0),
      'FECHA PRIMERA CONTRATACIÓN': formatDate(row.activationDate),
      'COMIENZO ÚLTIMA CONEXIÓN': formatDate(row.gprsStatus?.lastConnStart, true),
      'FIN ÚLTIMA CONEXIÓN': formatDate(row.gprsStatus?.lastConnStop, true),
      IMEI: row.imei || '',
      'COMPAÑÍA DE RED': row.presence?.sgsn?.operator?.company || '',
      'CÓDIGO DE PAÍS': row.presence?.sgsn?.operator?.countryCode || '',
      'TIPO DE RED': formatRatType(row.ratType),
      'TIPO DE TARIFA': row.pool_name || ''
    }));
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    if (location.state?.filter === true) {
      dispatch({
        type: 'SET_TABLE_STATE',
        payload: {
          filters: { alarms: [1, 2] },
          filteredRows: []
        }
      });
    }
  }, [location]);

  useEffect(() => {
    Pfetch();
  }, [Pfetch]);

  useEffect(() => {
    initCatalogDerivedState();
  }, [initCatalogDerivedState]);

  // ===== 8. COLUMNAS =====
  const columns = [
    {
      title: lang.handle('Alias'),
      dataIndex: 'alias',
      sorter: (a, b) => (a.alias || '').localeCompare(b.alias || ''),
      ...GetColumnLiteralSearchProps('alias', lang),
      filteredValue: state.activeFilters['alias'] || null,
    },
    {
      title: lang.handle('Organización'),
      dataIndex: 'customField1',
      filters: state.tenants.map(tenant => ({ 
        text: String(tenant.name).toUpperCase(), 
        value: String(tenant.name).toUpperCase() 
      })),
      filteredValue: state.activeFilters['customField1'] || null,
      onFilter: (value, record) => record.customField1 && record.customField1.toLowerCase() === value.toLowerCase(),
    },
    {
      title: 'ICC',
      dataIndex: 'icc',
      ...GetColumnLiteralSearchProps('icc', lang),
      filteredValue: state.activeFilters['icc'] || null,
    },
    {
      title: lang.handle('Instalación original'),
      dataIndex: 'device_name',
      ...GetColumnLiteralSearchProps('device_name', lang),
      filteredValue: state.activeFilters['device_name'] || null,
    },
    {
      title: 'MSISDN',
      dataIndex: 'msisdn',
      sorter: (a, b) => a.msisdn - b.msisdn,
      ...GetColumnLiteralSearchProps('msisdn', lang),
      filteredValue: state.activeFilters['msisdn'] || null,
    },
    {
      title: lang.handle('Estado'),
      dataIndex: 'lifeCycleStatus',
      filters: [
        { text: lang.handle('Desactivada'), value: 'DEACTIVATED' },
        { text: lang.handle('Activada'), value: 'ACTIVE' },
        { text: lang.handle('Inactiva nueva'), value: 'INACTIVE_NEW' },
        { text: lang.handle('Test'), value: 'TEST' },
        { text: lang.handle('Lista para activación'), value: 'ACTIVATION_READY' }
      ],
      filteredValue: state.activeFilters['lifeCycleStatus'] || null,
      onFilter: (value, record) => record.lifeCycleStatus && record.lifeCycleStatus.toLowerCase() === value.toLowerCase(),
      render: (data) => {
        switch (data) {
          case "DEACTIVATED": return <Tag color='red'> {lang.handle('Desactivada')} </Tag>;
          case "ACTIVE": return <Tag color='green'> {lang.handle('Activada')} </Tag>;
          case "INACTIVE_NEW": return <Tag color='gray'> {lang.handle('Inactiva nueva')} </Tag>;
          case "TEST": return <Tag color='blue'> {lang.handle('Test')} </Tag>;
          case "ACTIVATION_READY": return <Tag color='orange'> {lang.handle('Lista para activación')} </Tag>;
          default: return <Tag color='Black'> {lang.handle('Desconocido')} </Tag>;
        }
      },
    },
    {
      title: lang.handle('Alarmas'),
      dataIndex: "alarms",
      filters: M2M_ALARMS_STATE,
      onFilter: (value, record) => record.alarms?.includes(value) || false,
      filterIcon: <FilterFilled />,
      filteredValue: state.activeFilters['alarms'] || null,
      render: (data) => {
        let tags = [];
        (data || []).forEach(value => {
          const found = M2M_ALARMS_TAG.find(tag => tag.value === value);
          if (found) tags.push(found.label);
        });
        return tags;
      },
    },
    {
      title: lang.handle('Consumo diario'),
      dataIndex: ['consumptionDaily', 'data', 'value'],
      sorter: (a, b) => (a.consumptionDaily?.data?.value || 0) - (b.consumptionDaily?.data?.value || 0),
      render: (data) => <p> {formatBytes(data || 0)} </p>
    },
    {
      title: lang.handle('Consumo mensual'),
      dataIndex: ['consumptionMonthly', 'data', 'value'],
      sorter: (a, b) => (a.consumptionMonthly?.data?.value || 0) - (b.consumptionMonthly?.data?.value || 0),
      render: (data) => <p> {formatBytes(data || 0)} </p>
    },
    {
      title: lang.handle('Fecha primera contratación'),
      dataIndex: 'activationDate',
      sorter: (a, b) => (a.activationDate ? dayjs(a.activationDate).valueOf() : 0)
                      - (b.activationDate ? dayjs(b.activationDate).valueOf() : 0),
      render: (data) => <p> {formatDate(data)} </p>
    },
    {
      title: lang.handle('Comienzo última conexión'),
      dataIndex: ['gprsStatus', 'lastConnStart'],
      render: (data) => <p> {formatDate(data, true)} </p>
    },
    {
      title: lang.handle('Fin última conexión'),
      dataIndex: ['gprsStatus', 'lastConnStop'],
      render: (data) => <p> {formatDate(data, true)} </p>
    },
    {
      title: 'IMEI',
      dataIndex: 'imei',
    },
    {
      title: lang.handle('Compañía de red'),
      dataIndex: ['presence', 'sgsn', 'operator', 'company'],
    },
    {
      title: lang.handle('Código de país'),
      dataIndex: ['presence', 'sgsn', 'operator', 'countryCode'],
    },
    {
      title: lang.handle('Tipo de red'),
      dataIndex: 'ratType',
      filters: [
        { text: '3G', value: 1 },
        { text: '2G', value: 2 },
        { text: '3.5G', value: 5 },
        { text: '4G', value: 6 },
        { text: 'NB-IoT', value: 8 }
      ],
      filteredValue: state.activeFilters['ratType'] || null,
      onFilter: (value, record) => record.ratType && record.ratType.toString().indexOf(value) === 0,
      render: (data) => <p> {formatRatType(data)} </p>
    },
    {
      title: lang.handle('Pool'),
      dataIndex: 'pool_name',
      filters: state.poolNamesFilter,
      filteredValue: state.activeFilters['pool_name'] || null,
      onFilter: (value, record) => record.pool_name && record.pool_name.toLowerCase() === value.toLowerCase(),
      render: (data) => data || lang.handle('Sin asignar'),
    },
  ];

  const chartOptions = {
    scales: {
      y: {
        ticks: {
          callback: (value) => `${value} MB`
        }
      }
    }
  }

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      dispatch({ type: 'SET_SELECTED_IDS', payload: selectedRowKeys });
    }
  };

  // ===== 9. RENDER =====
  return (
    <div>
      {state.iccAlerts.length > 0 && <Alert
        message={lang.handle("Tarjetas desactivadas desde hace mas de 12 meses")}
        description={state.alertDescription}
        type="warning"
        action={
          <Button onClick={openInfo}>
            {lang.handle('Ver más')}
          </Button>
        }
        closable
      />}
      
      <DataTable
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        columns={columns}
        dataSource={state.datos}
        loading={state.loading}
        rowKey="icc"
        onChange={handleTableChange}
      />
      
      <div className="button-panel">
        <Space>
        <Excelexport
          fileName={'m2m_list_' + dayjs().format('YYYYMMDD_HHmmss')}
          fileData={() => [{ name: 'm2m', data: getExportData() }]}
          disabled={(state.filteredRows.length || state.datos.length) === 0}
          label={lang.handle('Exportar a Excel')}
        />

          {hasPrivilege('m2m_cicle') && <Button
            type="primary"
            id="lifeCycle"
            icon={<MobileOutlined />}
            disabled={singleSelectDisabled}
            onClick={showModal}>
            {lang.handle('Cambiar ciclo vida')}
          </Button>}

          {hasPrivilege('m2m_state') && <Button
            type="primary"
            id="state"
            icon={<MobileOutlined />}
            disabled={singleSelectDisabled}
            onClick={showModal}>
            {lang.handle('Estado')}
          </Button>}

          <Button
            type="primary"
            id="consume"
            icon={<MobileOutlined />}
            disabled={singleSelectDisabled}
            onClick={showModal}>
            {lang.handle('Consumo')}
          </Button>

          {hasPrivilege('m2m_pool') && <Button
            type="primary"
            icon={<MobileOutlined />}
            disabled={poolButtonDisabled}
            onClick={() => dispatch({ type: 'SHOW_MODAL_POOL', payload: true })}>
            {lang.handle('Cambiar tarifa')}
          </Button>}
        </Space>
      </div>

      {/* Modal cambiar estado SIM */}
      <Modal
        title={lang.handle('Cambiar estado de la SIM') + (state.selectedIds[0] ? ` - ${state.selectedIds[0]}` : '')}
        onCancel={handleCancel}
        cancelText={lang.handle('Cerrar')}
        onOk={onSubmit}
        okText={lang.handle('Cambiar')}
        okButtonProps={{ loading: state.loading }}
        icon={<ExclamationCircleOutlined />}
        open={state.showModal}>
        {lang.handle('Por favor, seleccione una opción')}
        <Form>
          <Form.Item>
            <Select
              onChange={handleChange}
              value={state.lifeCycle || undefined}
              placeholder={lang.handle('Selecciona un estado')}
              options={lifeCycleOptions} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal estado SIM */}
      <Modal
        title={lang.handle('Estado de la SIM') + (state.selectedIds[0] ? ` - ${state.selectedIds[0]}` : '')}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={handleCancel}
        onOk={handleCancel}
        okText={lang.handle('Cerrar')}
        loading={state.loading}
        open={state.showModalState}>
        <Spin spinning={state.loading}>
          <div className="row">
            <div className="col-4 arrow d-flex justify-content-around" style={{ backgroundColor: state.featuresGSM.background, color: 'white', fontWeight: 'bold' }}>
              {state.featuresGSM.icon}
              {lang.handle('Estado GSM')}
            </div>
            <div className="col-4 text-center">
              {state.featuresGSM.text}
            </div>
            <div className="col-4">
              {state.stateData.responseGSM?.result}
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-4 arrow d-flex justify-content-around" style={{ backgroundColor: state.featuresGPRS.background, color: 'white', fontWeight: 'bold' }}>
              {state.featuresGPRS.icon}
              {lang.handle('Estado GPRS')}
            </div>
            <div className="col-4 text-center">
              {state.featuresGPRS.text}
            </div>
            <div className="col-4">
              {state.stateData.responseGPRS?.result}
            </div>
          </div>
        </Spin>
      </Modal>

      {/* Modal consumo */}
      <Modal
        title={lang.handle('Consumo de la SIM') + (state.selectedIds[0] ? ` - ${state.selectedIds[0]}` : '')}
        onCancel={handleCancel}
        cancelText={lang.handle('Cerrar')}
        icon={<ExclamationCircleOutlined />}
        open={state.showModalConsume}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {lang.handle('Cerrar')}
          </Button>
        ]}
        width={800}
      >
        <Spin spinning={state.loading}>
          <div>
            <Space size={20}>
              <RangePicker
                minDate={dayjs().subtract(1, 'year')}
                maxDate={dayjs()}
                onChange={handleDateChange}
                value={[
                  dayjs(state.params.start_date),
                  dayjs(state.params.end_date)
                ]} />
              <Switch 
                checkedChildren={lang.handle("Mensual")} 
                unCheckedChildren={lang.handle("Diario")} 
                checked={state.params.monthly} 
                disabled 
              />
            </Space>
            <BarChart 
              data={state.consumes} 
              labels={state.dates} 
              options={chartOptions} 
              dataLabel="Consumo" 
            />
          </div>
        </Spin>
      </Modal>

      {/* Modal cambiar tarifa (pool) */}
      <Modal
        title={lang.handle('Cambiar tarifa') + ` (${state.selectedIds.length})`}
        onCancel={handleCancel}
        cancelText={lang.handle('Cerrar')}
        onOk={onSubmitPool}
        okText={lang.handle('Cambiar')}
        okButtonProps={{ loading: state.loading, disabled: !state.selectedPool }}
        icon={<ExclamationCircleOutlined />}
        open={state.showModalPool}>
        {lang.handle('Selecciona la tarifa destino para las tarjetas seleccionadas')}
        <Form>
          <Form.Item>
            <Select
              showSearch
              optionFilterProp="label"
              onChange={(value) => dispatch({ type: 'SET_SELECTED_POOL', payload: value })}
              value={state.selectedPool || undefined}
              placeholder={lang.handle('Seleccionar opción')}
              options={poolOptions}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default withLocale(withNavigation(M2MList));
